<template>
  <v-container fluid class="classification-bar">
    <h3 class="intake-section-label">Classification</h3>
    <GuessMetadataHeader v-if="rowGuessMeta(PATIENT_IDENTITY_ROW_FIELDS)" />
    <v-row dense>
      <v-col cols="6">
        <v-select
          label="Classification"
          :items="classifications"
          :model-value="intakeStore.pageManager?.currentPage?.documentType"
          :disabled="!props.editable"
          @update:model-value="onDocumentTypeChange" />
      </v-col>
      <v-col v-if="showDueDate" cols="4">
        <text-date-input
          :max-date="null"
          label="Due Date"
          :model-value="intakeStore.pageManager?.currentPage?.dueDate"
          :disabled="!props.editable"
          @update:model-value="onDocumentDueDateChanged" />
      </v-col>
    </v-row>
    <v-row v-if="props.editable || props.showSplitButton" dense class="classification-bar__actions">
      <v-col cols="auto">
        <v-btn
          v-if="props.editable"
          variant="tonal"
          color="primary"
          rounded
          prepend-icon="mdi-file-document-multiple-outline"
          class="classification-bar__btn"
          @click="onDocumentClassifyEntirely">
          Classify Entire Document
        </v-btn>
      </v-col>
      <v-col cols="auto">
        <v-btn
          v-if="props.showSplitButton"
          variant="tonal"
          color="primary"
          rounded
          prepend-icon="mdi-content-cut"
          class="classification-bar__btn"
          @click="onSplitDocumentManually">
          Split Document Manually
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
  <v-dialog v-model="showSplitDocumentManually" max-width="95%">
    <DocumentSplit :on-close="() => (showSplitDocumentManually = false)" />
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useIntakeStore } from "@/stores/intakeStore";
import { useRequirementsStore } from "@/stores";
import { intakeService } from "@/services/intakeService";
import { formatDateToString } from "@/utils";
import { PATIENT_IDENTITY_ROW_FIELDS, useGuessMetadata } from "@/composables/useGuessMetadata";
import DocumentSplit from "./DocumentSplit.vue";
import GuessMetadataHeader from "./GuessMetadataHeader.vue";

const props = defineProps({
  editable: {
    required: false,
    type: Boolean,
    default: false,
  },
  showSplitButton: {
    required: false,
    type: Boolean,
    default: false,
  },
  patientGuess: {
    required: false,
    type: Object,
    default: null,
  },
});
const isDirty = ref(false);
const showSplitDocumentManually = ref(false);

const intakeStore = useIntakeStore();
const requirementsStore = useRequirementsStore();

const classifications = computed(() => {
  return requirementsStore.clientConfig?.intake.documentPageClassifications ?? [];
});

const showDueDate = computed(() => {
  return requirementsStore.clientConfig?.intake.showClassificationDueDate ?? false;
});

const { rowGuessMeta } = useGuessMetadata(() => props.patientGuess);

const onDocumentTypeChange = (value) => {
  if (intakeStore.pageManager?.currentPage) {
    intakeStore.pageManager.currentPage.updateAttribute("documentType", value);
    intakeStore.pageManager.currentPage.debouncedSaveUpdates();
    isDirty.value = true;
  }
};

const onDocumentDueDateChanged = (value) => {
  if (intakeStore.pageManager?.currentPage) {
    const currentClassification = intakeStore.pageManager?.currentPage?.documentType;
    intakeStore.pageManager?.pages?.forEach((page) => {
      if (page.documentType === currentClassification) {
        page.updateAttribute("dueDate", value);
        page.debouncedSaveUpdates();
      }
    });
    isDirty.value = true;
  }
};

const onDocumentClassifyEntirely = () => {
  const selectedClassification = intakeStore.pageManager?.currentPage?.documentType;
  const dueDate = intakeStore.pageManager?.currentPage?.dueDate;
  if (!selectedClassification && !dueDate) return;
  const updates = [];
  intakeStore.pageManager?.pages?.forEach((page) => {
    const pageUpdate = { id: page.id };
    let hasChanges = false;
    if (selectedClassification && page.documentType !== selectedClassification) {
      page.documentType = selectedClassification;
      pageUpdate.documentType = selectedClassification;
      hasChanges = true;
    }
    if (dueDate && page.dueDate !== dueDate) {
      page.dueDate = dueDate;
      pageUpdate.dueDate = dueDate;
      hasChanges = true;
    }
    if (hasChanges) updates.push(pageUpdate);
  });
  if (updates.length > 0) {
    const documentKey = intakeStore.pageManager?.pages?.[0]?.documentKey;
    const formatted = updates.map((u) => {
      if (u.dueDate instanceof Date) return { ...u, dueDate: formatDateToString(u.dueDate) };
      return u;
    });
    intakeStore.pendingPageSave = intakeService.bulkPatchPages(documentKey, formatted);
  }
};

const onSplitDocumentManually = () => {
  showSplitDocumentManually.value = true;
};

watch(
  () => intakeStore.pageManager?.currentPage,
  (newVal, oldVal) => {
    if (oldVal) {
      // If the user has edited the descriptor, save it before the current page is changed
      if (isDirty.value) {
        oldVal.saveUpdates();
        isDirty.value = false;
      }
    }
  }
);
</script>

<style lang="scss" scoped>
.classification-bar {
  .intake-section-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: $lh_font_family_dm_sans;
    font-size: 12.5px;
    line-height: 16px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: $lh_color_blue_gray;
    margin: 0 0 12px;
  }

  .classification-bar__actions {
    margin-top: 10px;
  }

  .classification-bar__btn {
    letter-spacing: normal;
    text-transform: none;
    font-weight: 500;
  }
}
</style>
